import React from 'react';
import { useSelector } from 'react-redux';
import { IStorefrontSettings } from '../../../models/Storefront';
import { Callout, Checkbox, DefaultButton, Icon, Toggle } from '@fluentui/react';
import { useGetOrganizationEntitlementsQuery } from '../../../store/endpoints/OrganizationsEndpoints';
import { localizerFor } from './localizationConfig';
import { RootState } from '../../../store/rootReducer';
import {
    IAutocompleteColumns, IAutocompleteConfig, SearchBarContext,
    autocompleteColumnEnabled, readAutocompleteConfig, writeAutocompleteConfig,
} from './autocompleteConfig';

const CONTEXT_LABELS: Record<SearchBarContext, string> = {
    header: 'header search bar',
    homePage: 'home page search bar',
    searchPage: 'search page search bar',
};

const COLUMN_LABELS: { key: keyof IAutocompleteColumns; label: string }[] = [
    { key: 'keywords', label: 'Keywords, categories and part numbers' },
    { key: 'manufacturers', label: 'Manufacturers' },
    { key: 'tags', label: 'Tags' },
    { key: 'products', label: 'Suggested products' },
];

interface IStorefrontSearchBarEditorProps {
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    /** Which bar is being edited. Decides which config blob the autocomplete settings read and write. */
    context: SearchBarContext;
    editMode?: boolean;
    /**
     * Opens this bar's suggestion-panel style panel. Owned by the parent editor so the same panel can
     * also be reached from its command bar, without two components racing to own its open state.
     */
    onEditStyles?: () => void;
    style?: React.CSSProperties;
    rootClassName?: string;
    isDark?: boolean;
}

const StorefrontSearchBarEditor: React.FC<IStorefrontSearchBarEditorProps> = (props) => {
    const targetRef = React.useRef<HTMLDivElement>(null);
    const [calloutOpen, setCalloutOpen] = React.useState(false);
    // The faux input's placeholder is shopper-facing text, so it comes from the storefront's
    // localization overrides like every other label the preview draws.
    const L = React.useMemo(
        () => localizerFor(props.storefrontSettings.localizationConfig),
        [props.storefrontSettings.localizationConfig]);
    const [tempSettings, setTempSettings] = React.useState(props.storefrontSettings);

    React.useEffect(() => {
        setTempSettings(props.storefrontSettings);
    }, [props.storefrontSettings]);

    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const entitlements = useGetOrganizationEntitlementsQuery(org ?? '', { skip: !org || org === '' });
    // Gated on the entitlement itself rather than a dedicated permission: nothing about autocomplete
    // needs to be delegated separately from the rest of storefront administration, and showing a toggle
    // the server would silently force back off is worse than not showing it.
    const canAutocomplete = entitlements.data?.searchAutocompleteEnabled === true;

    const autocomplete: IAutocompleteConfig = readAutocompleteConfig(tempSettings, props.context);

    // Edits tempSettings and lets closeCallout commit, like every other control here.
    //
    // It used to write straight through to the parent instead, which quietly discarded any pending
    // change beside it: committing re-rendered the parent, the effect above resynced tempSettings from
    // the new props, and a "Show search in header" toggle that had not been committed yet was overwritten
    // before closeCallout could compare it. Toggle the header switch, then any autocomplete option, and
    // the header switch silently reverted.
    //
    // The functional update keeps the property the write-through version was after -- each patch reads
    // the freshest state rather than whatever a stale closure captured -- without the split-brain of
    // reading tempSettings and writing props.
    const updateAutocomplete = (patch: Partial<IAutocompleteConfig>) => {
        setTempSettings(prev => writeAutocompleteConfig(
            prev, props.context, { ...readAutocompleteConfig(prev, props.context), ...patch }));
    };

    const updateColumn = (key: keyof IAutocompleteColumns, checked: boolean) =>
        updateAutocomplete({ columns: { ...autocomplete.columns, [key]: checked } });

    const closeCallout = React.useCallback(() => {
        // Compare every field the callout can edit — checking only useHeaderSearch would silently
        // discard a change to any of the other settings.
        const changed = (Object.keys(tempSettings) as (keyof IStorefrontSettings)[])
            .some(key => tempSettings[key] !== props.storefrontSettings[key]);
        if (changed) props.onStorefrontSettingsChange(tempSettings);
        setCalloutOpen(false);
    }, [tempSettings, props]);

    // Leaving edit mode with the callout still open would unmount it without ever committing, losing
    // whatever the merchant had just toggled. Commit and close instead of dropping it.
    React.useEffect(() => {
        if (!props.editMode && calloutOpen) closeCallout();
    }, [props.editMode, calloutOpen, closeCallout]);

    // Mirror the live storefront's search box (ac-storefront-web NavBar/HomePage): a Fluent v9
    // "outline" Input, size large, with the search icon trailing (contentAfter). The admin app
    // is Fluent v8, so we render a faux input with the same look and spread the same searchBar
    // style config the storefront applies — so the preview matches what shoppers see.
    return <div className={props.rootClassName}>
        <div
            ref={targetRef}
            // Editing is edit-mode only. Outside it this is a preview of the shopper's search bar, and
            // the surrounding editor defers its save until edit mode exits — so a change made here in
            // preview mode would never be written back, silently discarding the merchant's edit.
            onClick={props.editMode ? () => setCalloutOpen(true) : undefined}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxSizing: 'border-box',
                width: '100%',
                minWidth: 200,
                minHeight: 40,
                padding: '0 12px',
                background: '#ffffff',
                color: '#707070',
                border: '1px solid #d1d1d1',
                borderBottomColor: '#616161',
                borderRadius: 4,
                fontSize: 16,
                cursor: props.editMode ? 'pointer' : 'default',
                ...props.style,
            }}
        >
            <span style={{ flex: 1, fontSize: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {L('localization_nav_search_placeholder')}
            </span>
            <Icon iconName='Search' styles={{ root: { fontSize: 16, color: 'inherit' } }} />
        </div>
        {calloutOpen && props.editMode && <Callout
            target={targetRef.current}
            onDismiss={closeCallout}
            style={{ padding: '1em' }}
        >
            <h3>Search Settings</h3>
            {/* Header-only: this is what decides whether the header carries a search bar at all, and
                the home page hero bar stands in when it does not. It has no meaning on the search page,
                which always has its own bar. */}
            {props.context === 'header' && <Toggle
                inlineLabel
                label={'Show Search Bar in Header'}
                checked={tempSettings.useHeaderSearch ?? false}
                onChange={(_, checked) => setTempSettings({ ...tempSettings, useHeaderSearch: checked })}
            />}
            {canAutocomplete && <>
                {/* The storefront-wide "Build Search Suggestions" switch lives on the storefront's
                    Products settings page, with the other product toggles. Without it there is nothing
                    to display, so say so rather than offering a per-bar toggle that would do nothing. */}
                {!tempSettings.enableSearchAutocomplete && <p style={{ margin: '0.5em 0 0 0', fontSize: 12, color: '#605e5c', maxWidth: 320 }}>
                    Search suggestions are not being built for this storefront. Turn on <strong>Build
                    Search Suggestions</strong> under Settings &rarr; Storefront &rarr; Products first.
                </p>}

                {tempSettings.enableSearchAutocomplete && <>
                    <Toggle
                        inlineLabel
                        label={`Show suggestions on the ${CONTEXT_LABELS[props.context]}`}
                        checked={autocomplete.enabled ?? false}
                        onChange={(_, checked) => updateAutocomplete({ enabled: !!checked })}
                        styles={{ root: { marginTop: 8 } }}
                    />
                    <p style={{ margin: '0.25em 0 0.5em 0', fontSize: 12, color: '#605e5c', maxWidth: 320 }}>
                        Suggestions are rebuilt nightly, so a newly enabled storefront shows nothing
                        until the next run. Columns with no matches are hidden automatically.
                    </p>

                    {autocomplete.enabled && <div style={{ maxWidth: 340 }}>
                        {COLUMN_LABELS.map(({ key, label }) => (
                            <Checkbox
                                key={key}
                                label={label}
                                checked={autocompleteColumnEnabled(autocomplete.columns, key)}
                                onChange={(_, checked) => updateColumn(key, !!checked)}
                                styles={{ root: { marginBottom: 6 } }}
                            />
                        ))}
                        {/* Styling opens its own panel rather than living here: seven style parts with
                            the full control set overwhelmed a callout anchored to the input, covering
                            the very preview being styled. */}
                        {props.onEditStyles && <DefaultButton
                            text='Suggestion Panel Styles'
                            iconProps={{ iconName: 'Color' }}
                            onClick={() => { closeCallout(); props.onEditStyles?.(); }}
                            styles={{ root: { marginTop: 8 } }}
                        />}
                    </div>}
                </>}
            </>}
        </Callout>}
    </div>;
};

export default StorefrontSearchBarEditor;
